import React, { useMemo } from "react";
import moment from "moment";
import KycService from "../../services/kyc-service";

export const KYC_DEFAULT_FILTER_PAYLOAD = {
	fromDate: "2026-04-09T12:10:21.201Z",
	toDate: "2026-04-09T12:10:21.201Z",
	distributionChannel: "",
	priceGroup: "",
	salesDistrict: "",
	salesOffice: "",
	country: "",
	director: "",
	generalManager: "",
	continentalManager: "",
	regionalManager: "",
	areaManager: "",
	customerGroup: "",
	materialGroup: "",
	materialType: "",
	materialDivision: "",
	materialStructure: "",
	materialCharacteristics: "",
	materialHierarchy1: "",
	materialHierarchy2: "",
	materialHierarchy3: "",
};

const SECTION_LABELS = {
	GeneralDetails: "General",
	DesignFeatures: "Design Feature",
	CylinderFabrication: "Fabrication",
};

const FIELD_LABEL_OVERRIDES = {
	designMasterNumber: "Design Master No",
	designMasterDate: "Design Master Date",
	designAssignTo: "Design Assigned To",
	contact: "Contact",
	designType: "Design Type",
	designDetail: "Design Detail",
	pinchType: "Pinch Type",
	directionType: "Direction Type",
	designThrough: "Design Through",
	customerDesignReceived: "Customer Design Received",
	fabricType: "Fabric Type",
	gusset_KPPL: "Gusset(KPPL)",
	backseam: "Backseam",
	handle_Type_KPPL: "Handle Type(KPPL)",
	typeOfPrinting: "Type of Printing",
	ezOpen: "Ez Open",
	flexo_Screen_Printing: "Flexo/Screen Printing",
	flexoPrintingType: "Flexo Printing Type",
	flexo_Gusset_Printing: "Flexo Gusset Printing",
	flexoType: "Flexo Type",
	bagStructure: "Bag Structure",
	holographic: "Holographic",
	comments: "Comments",
	bag_Pouch_Size: "Bag/Pouch Size",
	design_Height_mm: "Design Height (mm)",
	gusset_mm: "Gusset",
	cylinder_Size_Width_mm: "Cylinder Size Width (mm)",
	cylinder_Size_Circum_mm: "Cylinder Size Circumference (mm)",
	cylinderEngraving: "Cylinder Engraving",
	cyl_Printing_Direction: "Cylinder Printing Direction",
	cylinderType: "Cylinder Type",
	upS_Coil_Of_Cylinder: "UPS (Coil) Dr Cylinder",
	no_Of_Repeats: "Number of Repeats",
	cylinder_Flexo_Payment_By: "Cylinder/Flexo Payment By",
	noofProof: "Number of Proofs",
	material: "Material",
	cylinderFabricationNo: "Cylinder/Fabrication No.",
	cylinderFabricationDate: "Cylinder/Fab. Date",
	totalNoOfCylinder: "Total No. of Cylinder",
	cylinder_Size_L_Circumference: "Cylinder Size (L-Circumference)",
	cylinderSize_Width: "Cylinder Size (Width)",
	printingType: "Printing Type",
	totalDesign_Size_W_X_L_Circumference: "Total Design Size (WXL)",
	typeOfCylinder: "Type of Cylinder",
	vendor: "Vendor",
};

const LABEL_ACRONYMS = new Set(["GSM", "GPM", "MM", "KPPL", "UPS", "WXL", "EZ"]);

const formatDesignDate = (value) => {
	if (!value || value === "-") {
		return "-";
	}
	const parsedDate = moment(
		value,
		["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD", moment.ISO_8601, "M/D/YYYY h:mm:ss A"],
		true
	);
	return parsedDate.isValid() ? parsedDate.format("DD/MM/YYYY") : String(value);
};

const formatFieldValue = (value) => {
	if (value === null || value === undefined || value === "") {
		return "-";
	}
	if (value === true || String(value).toLowerCase() === "true") {
		return "Yes";
	}
	if (value === false || String(value).toLowerCase() === "false") {
		return "No";
	}
	return String(value);
};

const formatFieldLabel = (label) => {
	const rawLabel = String(label || "").trim().replace(/\s+/g, " ");
	if (!rawLabel) {
		return "";
	}

	return rawLabel.replace(/[A-Za-z]+/g, (word) => {
		const upper = word.toUpperCase();
		if (LABEL_ACRONYMS.has(upper)) {
			return upper;
		}
		return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
	});
};

const formatKeyAsLabel = (key) => {
	if (FIELD_LABEL_OVERRIDES[key]) {
		return FIELD_LABEL_OVERRIDES[key];
	}
	const stripped = String(key || "")
		.replace(/_/g, " ")
		.replace(/([a-z])([A-Z])/g, "$1 $2");
	return formatFieldLabel(stripped);
};

const formatSectionName = (sectionKey) => SECTION_LABELS[sectionKey] || formatKeyAsLabel(sectionKey);
const isDateField = (key) => /date/i.test(String(key || ""));

const resolveFieldValue = (record, key) => {
	const value = record?.[key];
	if (isDateField(key)) {
		return formatDesignDate(value);
	}
	return formatFieldValue(value);
};

const splitIntoColumns = (items, columnCount) => {
	const columns = Array.from({ length: columnCount }, () => []);
	items.forEach((item, index) => {
		columns[index % columnCount].push(item);
	});
	return columns;
};

const getSectionFieldBindings = (sectionData = {}) =>
	Object.keys(sectionData).map((key) => [formatKeyAsLabel(key), key]);

export const parseSectionsFromRecord = (record, keyPrefix = "") => {
	if (!record?.sections || !Array.isArray(record.sections)) {
		return [];
	}

	return record.sections
		.map((sectionItem, index) => {
			const sectionKey = Object.keys(sectionItem || {})[0];
			if (!sectionKey) {
				return null;
			}
			return {
				sectionKey,
				sectionName: formatSectionName(sectionKey),
				groupKey: keyPrefix ? `${keyPrefix}-${sectionKey}-${index}` : `${sectionKey}-${index}`,
				sectionData: sectionItem[sectionKey] || {},
			};
		})
		.filter(Boolean);
};

export const parseSectionsFromResponse = (detailsList) => {
	const record = Array.isArray(detailsList) ? detailsList[0] : null;
	return parseSectionsFromRecord(record);
};

export const parseDesignMasterListResponse = (response) => {
	const success = response?.data?.statusCode === 200 || response?.data?.status === true;
	if (!success) {
		return [];
	}
	const responseData = response.data.responseData || {};
	const designList = Array.isArray(responseData)
		? responseData
		: responseData.desingMasterLists ||
			responseData.kycDesignMasterLists ||
			responseData.designMasterList ||
			responseData.data ||
			[];
	return Array.isArray(designList) ? designList : [];
};

export const parseDesignMasterTableResponse = (response, fallbackPage = 1) => {
	const rows = parseDesignMasterListResponse(response);
	const responseData = response?.data?.responseData || {};
	const totalRecords = Number(
		Array.isArray(responseData) ? rows.length : responseData.totalRecords || rows.length || 0
	);
	const rawPage = Array.isArray(responseData) ? undefined : responseData.currentPage;
	const parsedPage = Number(rawPage);
	const currentPage =
		rawPage !== undefined && rawPage !== null && rawPage !== "" && !Number.isNaN(parsedPage)
			? parsedPage
			: fallbackPage;
	return { rows, totalRecords, currentPage };
};

const extractDesignDetailsListFromResponse = (response) => {
	const success = response?.data?.statusCode === 200 || response?.data?.status === true;
	if (!success) {
		return [];
	}
	const responseData = response.data.responseData || {};
	const detailsList = Array.isArray(responseData)
		? responseData
		: responseData.desingDetailsLists ||
			responseData.desingDetailLists ||
			responseData.kycDesignDetailsLists ||
			responseData.designDetailsList ||
			responseData.data ||
			[];
	return Array.isArray(detailsList) ? detailsList : [];
};

export const parseDesignDetailsListResponse = (response) => {
	const detailsList = extractDesignDetailsListFromResponse(response);
	return parseSectionsFromResponse(detailsList);
};

export const parseDesignDetailsRecordsFromResponse = (response) => {
	const detailsList = extractDesignDetailsListFromResponse(response);
	return detailsList
		.map((record, index) => {
			const recordKey = String(record?.generalDetailId || index);
			const sections = parseSectionsFromRecord(record, recordKey);
			const generalSection = sections.find((section) => section.sectionKey === "GeneralDetails");
			const generalData = generalSection?.sectionData || {};
			return {
				generalDetailId: record?.generalDetailId,
				designMasterNumber: generalData.designMasterNumber || record?.designMasterNumber || `Design ${index + 1}`,
				sections,
				groupKey: recordKey,
			};
		})
		.filter((record) => record.sections.length > 0);
};

export const fetchDesignMasterList = async ({
	materialName = "",
	customer = "",
	crmGuid = "",
	search = "",
	pageindex = 1,
	pagesize = 10,
	sortcolumn = "designMasterDate",
	orderBy = "DESC",
	groupCompanyId = "",
} = {}) => {
	const requestData = {
		pageindex,
		pagesize,
		sortcolumn: sortcolumn || "",
		orderBy: orderBy || "",
		search: String(search || "").trim(),
		...KYC_DEFAULT_FILTER_PAYLOAD,
		materialName: String(materialName || "").trim(),
	};
	const trimmedCustomer = String(customer || "").trim();
	const trimmedCrmGuid = String(crmGuid || "").trim();
	const trimmedGroupCompanyId = String(groupCompanyId || "").trim();
	if (trimmedCustomer) {
		requestData.customer = trimmedCustomer;
	}
	if (trimmedCrmGuid) {
		requestData.crmGuid = trimmedCrmGuid;
	}
	if (trimmedGroupCompanyId) {
		requestData.groupcomanyId = trimmedGroupCompanyId;
	}
	const response = await KycService.getKycDesignMasterListAPI(requestData);
	return parseDesignMasterTableResponse(response, pageindex);
};

export const fetchDesignDetailsSections = async (generalDetailId) => {
	if (!generalDetailId) {
		return [];
	}
	const requestData = {
		pageindex: 1,
		pagesize: 10,
		sortcolumn: "",
		orderBy: "",
		search: "",
		...KYC_DEFAULT_FILTER_PAYLOAD,
		generalDetailId: Number(generalDetailId) || generalDetailId,
	};
	const response = await KycService.getKycDesignDetailsListAPI(requestData);
	return parseDesignDetailsListResponse(response);
};

export const fetchDesignDetailsByMaterial = async (material, search = "") => {
	const trimmedMaterial = String(material || "").trim();
	if (!trimmedMaterial) {
		return [];
	}
	const requestData = {
		pageindex: 1,
		pagesize: 50,
		sortcolumn: "",
		orderBy: "",
		search: String(search || "").trim(),
		...KYC_DEFAULT_FILTER_PAYLOAD,
		material: trimmedMaterial,
	};
	const response = await KycService.getKycDesignDetailsListAPI(requestData);
	return parseDesignDetailsRecordsFromResponse(response);
};

export const DesignSectionFieldGrid = ({ sectionData }) => {
	const fieldBindings = useMemo(() => getSectionFieldBindings(sectionData), [sectionData]);
	const columns = useMemo(() => splitIntoColumns(fieldBindings, 4), [fieldBindings]);

	if (!fieldBindings.length) {
		return <div className="kyc-tab-placeholder kyc-design-section-empty">No data available.</div>;
	}

	return (
		<div className="row kyc-mc-accordion-body">
			{columns.map((column, colIdx) => (
				<div className="col-lg-3 col-md-6 kyc-mc-column" key={`col-${colIdx}`}>
					{column.map(([label, key]) => (
						<div className="kyc-mc-field" key={key}>
							<div className="kyc-mc-field-label">{label}:</div>
							<div className="kyc-mc-field-value">{resolveFieldValue(sectionData, key)}</div>
						</div>
					))}
				</div>
			))}
		</div>
	);
};
